/* eslint-disable @typescript-eslint/member-ordering */

import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

export interface MultiFileUploadItem {
  id: string;
  file: File;
  error: string | null;
  progress: number;
}

export interface FormattedUploadFileSize {
  translationKey: string;
  size: number;
}

/** Usage: bind items and handle itemsChange; validation stays configurable and feature-agnostic. */
@Component({
  selector: 'app-multi-file-upload',
  templateUrl: './app-multi-file-upload.component.html',
  styleUrls: ['./app-multi-file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppMultiFileUploadComponent {
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;
  @Input() items: MultiFileUploadItem[] = [];
  @Input() disabled = false;
  @Input() accept = '';
  @Input() maxFiles = 0;
  @Input() maxFileSizeBytes = 0;
  @Input() sizeErrorKey = 'file_upload_size_invalid';
  @Input() duplicateErrorKey = 'file_upload_duplicate';
  @Input() maxFilesErrorKey = 'file_upload_max_files';
  @Input() sizeTextKbKey = 'file_size_kb';
  @Input() sizeTextMbKey = 'file_size_mb';
  @Input() chooseLabel = 'Choose files';
  @Input() dropHint = 'Choose files or drop them here';
  @Input() helperText = '';
  @Input() removeLabelKey = 'Remove {{file}}';
  @Output() itemsChange = new EventEmitter<MultiFileUploadItem[]>();

  private nextItemId = 0;

  addFiles(files: FileList | readonly File[] | null): void {
    if (!files || this.disabled) {
      return;
    }

    const existingKeys = new Set(this.items.map(item => this.fileKey(item.file)));
    let acceptedCount = this.items.filter(item => !item.error).length;
    const added = Array.from(files).map(file => {
      const key = this.fileKey(file);
      const duplicate = existingKeys.has(key);
      existingKeys.add(key);
      let error: string | null = null;
      if (duplicate) {
        error = this.duplicateErrorKey;
      } else if (this.maxFileSizeBytes > 0 && file.size > this.maxFileSizeBytes) {
        error = this.sizeErrorKey;
      } else if (this.maxFiles > 0 && acceptedCount >= this.maxFiles) {
        error = this.maxFilesErrorKey;
      } else {
        acceptedCount += 1;
      }

      return { id: `${key}-${this.nextItemId++}`, file, error, progress: 0 };
    });
    this.itemsChange.emit([...this.items, ...added]);
  }

  removeFile(id: string): void {
    if (!this.disabled) {
      this.itemsChange.emit(this.items.filter(item => item.id !== id));
    }
  }

  focusPicker(): void {
    this.fileInput?.nativeElement.focus();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.addFiles(event.dataTransfer?.files || null);
  }

  trackByItem(index: number, item: MultiFileUploadItem): string {
    return item.id;
  }

  formatFileSize(bytes: number): FormattedUploadFileSize {
    const megabyte = 1024 * 1024;
    return bytes < megabyte
      ? { translationKey: this.sizeTextKbKey, size: bytes / 1024 }
      : { translationKey: this.sizeTextMbKey, size: bytes / megabyte };
  }

  private fileKey(file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }
}
